import { Schema, type Node as ProseMirrorNode, type NodeSpec } from 'prosemirror-model';
import { PARAGRAPH_SCHEMA } from '@adornis/prosemirror-editor/nodes/paragraph/schema.js';
import { HARD_BREAK_SCHEMA } from '@adornis/prosemirror-editor/nodes/hard-break/schema.js';
import { BOLD_SCHEMA } from '@adornis/prosemirror-editor/marks/bold/schema.js';
import { ITALIC_SCHEMA } from '@adornis/prosemirror-editor/marks/italic/schema.js';

export type StructuredValue = null | boolean | number | string | StructuredValue[] | { [key: string]: StructuredValue };
export type StructureKind = 'site' | 'page' | 'locale' | 'audience' | 'section' | 'container' | 'repeat' | 'record';
export interface StructureMapping {
  kind?: (path: readonly string[], value: Record<string, StructuredValue>) => StructureKind;
  id?: (path: readonly string[], value: StructuredValue) => string;
}
const attrs = { key: { default: '' }, id: { default: '' } };
const record = (kind: string): NodeSpec => ({
  group: 'structured', content: 'structured*', attrs,
  toDOM: node => ['div', { 'data-web-kind': kind, 'data-web-id': node.attrs.id }, 0],
});
export const structuredDocumentSchema = new Schema({
  nodes: {
    doc: { content: 'structured' },
    site: record('site'), page: record('page'), locale: record('locale'), audience: record('audience'),
    section: record('section'), container: record('container'), repeat: record('repeat'), record: record('record'),
    sequence: { ...record('sequence') },
    field: { group: 'structured', content: 'inline*', attrs, toDOM: node => ['span', { 'data-web-id': node.attrs.id }, 0] },
    value: { group: 'structured', atom: true, attrs: { ...attrs, value: { default: null } }, toDOM: () => ['span', { hidden: true }] },
    paragraph: PARAGRAPH_SCHEMA(), text: { group: 'inline' }, hard_break: HARD_BREAK_SCHEMA(),
  },
  marks: { bold: BOLD_SCHEMA(), italic: ITALIC_SCHEMA() },
});

/** Lossless bridge for host models. Content is stored in child nodes, never as a project attribute. */
export const encodeStructuredDocument = (value: StructuredValue, mapping: StructureMapping = {}): ProseMirrorNode => {
  const schema = structuredDocumentSchema;
  const encode = (current: StructuredValue, path: string[], key: string): ProseMirrorNode => {
    const attributes = { key, id: mapping.id?.(path, current) ?? JSON.stringify(path) };
    if (typeof current === 'string') {
      const content: ProseMirrorNode[] = [];
      current.split('\n').forEach((line, index) => {
        if (index) content.push(schema.nodes.hard_break!.create());
        if (line) content.push(schema.text(line));
      });
      return schema.nodes.field!.create(attributes, content);
    }
    if (Array.isArray(current)) return schema.nodes.sequence!.create(attributes, current.map((child, index) => encode(child, [...path, String(index)], String(index))));
    if (current !== null && typeof current === 'object') {
      const kind = mapping.kind?.(path, current) ?? (path.length ? 'record' : 'site');
      return schema.nodes[kind]!.create(attributes, Object.entries(current).map(([name, child]) => encode(child, [...path, name], name)));
    }
    if (typeof current === 'number' && !Number.isFinite(current)) throw new Error('Non-finite document number.');
    if (current !== null && typeof current !== 'boolean' && typeof current !== 'number') throw new Error('Document values must be JSON.');
    return schema.nodes.value!.create({ ...attributes, value: current });
  };
  return schema.topNodeType.create(null, encode(value, [], ''));
};

export const decodeStructuredDocument = (doc: ProseMirrorNode): StructuredValue => {
  doc.check();
  const decode = (node: ProseMirrorNode): StructuredValue => {
    if (node.type.name === 'field') return node.textBetween(0, node.content.size, '', '\n');
    if (node.type.name === 'value') return node.attrs.value as StructuredValue;
    if (node.type.name === 'sequence') { const values: StructuredValue[] = []; node.forEach(child => values.push(decode(child))); return values; }
    const result: Record<string, StructuredValue> = {};
    node.forEach(child => {
      const key = String(child.attrs.key);
      if (Object.hasOwn(result, key)) throw new Error(`Duplicate document key: ${key}`);
      Object.defineProperty(result, key, { value: decode(child), enumerable: true, writable: true, configurable: true });
    });
    return result;
  };
  if (doc.type.name !== 'doc' || doc.childCount !== 1) throw new Error('Invalid structured document root.');
  return decode(doc.firstChild!);
};
